import * as React from "react";
import { ComponentType } from "react";
import { connect } from "react-redux";
import { routerActions } from "react-router-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { GroupsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import RouterHoc, { IRouterActions, IRouterProps } from "src/hoc/RouterHoc";
import * as GroupActions from "../../../actions/groups";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { FormFilterInput } from "../../../components/form";
import { RestrictedLayout } from "../../../components/layouts/index";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";

import "./GroupsPage.scss";

interface IGroupsPageParams {}

interface IGroupsPageProps extends IRouterProps<IGroupsPageParams> {
    // From state
    groups: Dtos.Group[];
    formFilter: IFormFilter;
    formLength: number;

    loadingGroups: boolean;
    loadGroupsSuccess: boolean;
    loadGroupsFailure: boolean;
    loadGroupsErrorCode: string;

    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
}

interface IGroupsPageActions extends IRouterActions {
    clearGroups: GroupActions.IGroupClearActionCreator;
    loadGroups: GroupActions.IGroupLoadActionCreator;

    navigate: typeof routerActions.push;
    navigateReplace: typeof routerActions.replace;

    filter: GroupActions.IGroupFilterActionCreator;

    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;

    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;

}

type GroupsPageProps = IGroupsPageProps & IGroupsPageActions;

class GroupsPage extends React.PureComponent<GroupsPageProps, any> {
    initialised: boolean;

    constructor(props: GroupsPageProps) {
        super(props);
        this.initialised = false;
        this.clearGroups = this.clearGroups.bind(this);
    }

    componentDidMount() {
        const { 
            loadGroups, 
            setDrawerItems, 
            setBreadcrumbs
        } = this.props;
        

        setDrawerItems([]);
        setBreadcrumbs(GroupsBreadcrumbs());

        loadGroups();
    }

    componentWillReceiveProps(_nextProps: GroupsPageProps) {}

    componentDidUpdate(_prevProps: GroupsPageProps, _prevState) {
        const {
            setDrawerItems,
            setBreadcrumbs,
            loadGroupsFailure,
            navigate,
            loadGroupsErrorCode,
        } = this.props;

        if (loadGroupsFailure) {
            navigate(`/error/${loadGroupsErrorCode}`);
        }

        if (!this.initialised) {
            setDrawerItems([]);
            setBreadcrumbs(GroupsBreadcrumbs());
            this.initialised = true;
        }
    }

    componentWillUpdate(_nextProps: GroupsPageProps) {}

    componentWillUnmount() {
        this.clearGroups();
    }

    clearGroups() {
        this.props.clearGroups();
    }

    showGroups(): boolean {
        const { loadingGroups, loadGroupsSuccess } = this.props;

        if (!loadingGroups && loadGroupsSuccess) {
            return true;
        }

        return false;
    }

    render() {
        const { loadingGroups } = this.props;

        return (
            <RestrictedLayout
                loading={loadingGroups}
                permission={Dtos.Permission.GroupView}
            >
                {this.renderContent()}
            </RestrictedLayout>
        );
    }

    renderContent() {
        const { permissions } = this.props;

        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Groups</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={this.props.formFilter}
                            onFilter={(e) => this.props.filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.GroupCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/groups/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!this.showGroups() ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {this.showGroups() ? (
                    <div className="card">
                        <div className="patient-tables">
                            {this.createTable()}
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }

    createTable() {
        const { groups, pageSize, setPageSize } = this.props;

        if (!groups || groups.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no groups to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = groups.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={groups}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={showPagination ? pageSize : groups.length}
                    pageSize={showPagination ? pageSize : groups.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no groups to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (e, handleOriginal) => {
                                const url: string =
                                    "/groups/" +
                                    rowInfo.original.id +
                                    "/details";
                                this.props.navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Duotone.GROUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "name",
                            Header: "Group Name",
                            accessor: "name",
                        },
                        {
                            id: "isDefault",
                            Header: "Default",
                            accessor: "isDefault",
                            maxWidth: 180,
                            Cell: (props) => {
                                return (
                                    <>
                                        <span className="truncate">
                                            {props.value == "" ? (
                                                ""
                                            ) : (
                                                <FontAwesomeIcon
                                                    size="lg"
                                                    icon={
                                                        FontAwesomeIcons.Light
                                                            .CHECK
                                                    }
                                                    fixedWidth
                                                />
                                            )}
                                        </span>
                                    </>
                                );
                            },
                        },
                    ]}
                />
            </div>
        );
    }
}

const mapStateToProps = (
    state: ITmdState,
    ownProps: GroupsPageProps
): IGroupsPageProps => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        match: ownProps.match,
        history: ownProps.history,
        location: ownProps.location,

        groups: state.groups.formFilteredData,
        formFilter: state.groups.formFilter,
        formLength: state.groups.formLength,

        loadingGroups:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Pending,
        loadGroupsSuccess:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Success,
        loadGroupsFailure:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Failure,
        loadGroupsErrorCode: state.groups.loadState.errorCode,

        user: state.user.data,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (
    dispatch,
    ownProps: GroupsPageProps
): IGroupsPageActions => {
    return {
        navigate: ownProps.navigate,
        navigateReplace: ownProps.navigateReplace,

        loadGroups: bindActionCreators(GroupActions.LoadGroup, dispatch),
        clearGroups: bindActionCreators(GroupActions.Clear, dispatch),

        filter: bindActionCreators(GroupActions.Filter, dispatch),

        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),

        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default RouterHoc<IGroupsPageParams, GroupsPageProps>(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(GroupsPage) as unknown as ComponentType<GroupsPageProps>
) as ComponentType<any>;
